import { ApplicationConfig, inject, provideAppInitializer , isDevMode, provideCheckNoChangesConfig } from '@angular/core';
import { provideRouter } from '@angular/router';
import { merge } from 'rxjs';
import { AuthService, provideAuth } from '@libs/auth';
import { VERSION_INFO } from '@libs/ui/version/version-info';
import { APP_INFO, AppInfo } from '@libs/ui/sign-in/app-info';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { versionInfo } from '../environments/version-info';
import { RoleMetadataService } from './services/role-metadata.service';

export const appConfig: ApplicationConfig = {
  providers: [
    // zoneless (default since v21): in dev, flag any binding that changed without a signal, event or markForCheck
    ...(isDevMode() ? [provideCheckNoChangesConfig({ exhaustive: true })] : []),
    provideRouter(routes),
    provideAuth({
      baseUrl: environment.baseUrl,
      // employee v3 (identity api-specs §10.1), since 2026-09-04: AMs are company employees on the v3 store.
      // the population is in the prefix (/company/), so the noun is just sessions: POST creates one,
      // :refresh re-mints it (identity `v3-auth-route-uniformity.md`). HTTP Basic sign-in and the refresh
      // token in the `access_token` header are what the shared AuthService already does. Cut an
      // environment over only once its AMs exist in employee v3 (task-login-employee-v3.md).
      signInPath: '/identity/company/v3/auth/sessions',
      refreshPath: '/identity/company/v3/auth/sessions:refresh',
      userField: 'employee',
      // My account (docs/ui/my-account.md): meaningful on a v3 token only, which the two lines above make it
      mePath: '/identity/company/v3/me',
      userStorageKey: 'STAFF',
      refreshTokenExpMinutes: environment.refreshTokenExpMinutes,
      loginPath: '/login',
      reloadOnLogout: false,
    }),
    { provide: VERSION_INFO, useValue: versionInfo },
    // the six values the shared sign-in page needs (docs/ui/sign-in.md §9). Values only: if the
    // two doors ever need different structure, fork the html rather than adding a seventh field.
    {
      provide: APP_INFO,
      useValue: {
        name: 'Juno',
        purpose: 'Client management console',
        credentialHint: 'Use your Friday Tech employee account.',
        homePath: '/businesses',
        lore: {
          title: 'Juno Moneta',
          story:
            'Queen of the Roman gods, and their warner — monere, to warn. Her temple on the ' +
            'Capitoline held the city mint, so her name is where the words money and mint come ' +
            'from. She is also the one who set Argus to watch.',
        },
      } satisfies AppInfo,
    },
    // a promotion or demotion rides in on the new token: drop the cached grantable roles (plan Q12)
    provideAppInitializer(() => {
      const auth = inject(AuthService);
      const roles = inject(RoleMetadataService);
      merge(auth.tokenRefreshed$, auth.loggedOut$).subscribe(() => roles.invalidate());
    }),
  ],
};
